import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LearningCalendar = ({ activityData }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0)?.getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1)?.getDay();
  };

  const getActivityLevel = (date) => {
    const dateStr = date?.toISOString()?.split('T')?.[0];
    const activity = activityData?.find(item => item?.date === dateStr);
    if (!activity) return 0;
    
    const minutes = activity?.minutesStudied;
    if (minutes >= 120) return 4; // Very high
    if (minutes >= 60) return 3;  // High
    if (minutes >= 30) return 2;  // Medium
    if (minutes > 0) return 1;    // Low
    return 0; // None
  };

  const getActivityColor = (level) => {
    switch (level) {
      case 4: return 'bg-primary';
      case 3: return 'bg-secondary';
      case 2: return 'bg-accent';
      case 1: return 'bg-muted';
      default: return 'bg-background border border-border';
    }
  };

  const getActivityLabel = (level) => {
    switch (level) {
      case 4: return 'Muito Alto (2h+)';
      case 3: return 'Alto (1-2h)';
      case 2: return 'Médio (30min-1h)';
      case 1: return 'Baixo (<30min)';
      default: return 'Nenhuma atividade';
    }
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate?.setMonth(prev?.getMonth() + direction);
      return newDate;
    });
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days?.push(
        <div key={`empty-${i}`} className="w-4 h-4 md:w-6 md:h-6" />
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const level = getActivityLevel(date);
      const isToday = date?.toDateString() === new Date()?.toDateString();
      const isSelected = selectedDate && date?.toDateString() === selectedDate?.toDateString();

      days?.push(
        <button
          key={day}
          onClick={() => setSelectedDate(date)}
          className={`w-4 h-4 md:w-6 md:h-6 rounded-sm transition-micro hover:scale-110 ${
            getActivityColor(level)
          } ${isToday ? 'ring-2 ring-primary ring-offset-1' : ''} ${
            isSelected ? 'ring-2 ring-secondary ring-offset-1' : ''
          }`}
          title={`${day}/${currentMonth?.getMonth() + 1} - ${getActivityLabel(level)}`}
        />
      );
    }

    return days;
  };

  const getSelectedDateActivity = () => {
    if (!selectedDate) return null;
    const dateStr = selectedDate?.toISOString()?.split('T')?.[0];
    return activityData?.find(item => item?.date === dateStr);
  };

  const selectedActivity = getSelectedDateActivity();

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-xl font-heading font-semibold text-foreground">
            Calendário de Atividades
          </h3>
          <p className="text-sm text-muted-foreground font-caption mt-1">
            Visualize seu padrão de aprendizado
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateMonth(-1)}
            iconName="ChevronLeft"
            iconSize={16}
          />
          <span className="text-sm font-medium text-foreground min-w-[120px] text-center">
            {currentMonth?.toLocaleDateString('pt-BR', { 
              month: 'long', 
              year: 'numeric' 
            })}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigateMonth(1)}
            iconName="ChevronRight"
            iconSize={16}
          />
        </div>
      </div>
      {/* Calendar Grid */}
      <div className="mb-6">
        {/* Week Days Header */}
        <div className="grid grid-cols-7 gap-2 mb-3">
          {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']?.map(day => (
            <div key={day} className="text-center text-xs font-medium text-muted-foreground font-caption">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-2">
          {renderCalendarDays()}
        </div>
      </div>
      {/* Legend */}
      <div className="mb-6">
        <p className="text-sm font-medium text-foreground mb-3">Nível de Atividade</p>
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground font-caption">Menos</span>
          <div className="flex items-center space-x-1">
            {[0, 1, 2, 3, 4]?.map(level => (
              <div
                key={level}
                className={`w-3 h-3 rounded-sm ${getActivityColor(level)}`}
                title={getActivityLabel(level)}
              />
            ))}
          </div>
          <span className="text-xs text-muted-foreground font-caption">Mais</span>
        </div>
      </div>
      {/* Selected Date Details */}
      {selectedDate && (
        <div className="pt-4 border-t border-border">
          <h4 className="text-lg font-heading font-semibold text-foreground mb-3">
            {selectedDate?.toLocaleDateString('pt-BR', { 
              weekday: 'long',
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
          </h4>
          
          {selectedActivity ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-3 bg-background rounded-lg">
                  <Icon name="Clock" size={20} className="text-primary mx-auto mb-1" />
                  <p className="text-lg font-heading font-semibold text-foreground">
                    {selectedActivity?.minutesStudied}min
                  </p>
                  <p className="text-xs text-muted-foreground font-caption">Tempo Estudado</p>
                </div>
                
                <div className="text-center p-3 bg-background rounded-lg">
                  <Icon name="BookOpen" size={20} className="text-secondary mx-auto mb-1" />
                  <p className="text-lg font-heading font-semibold text-foreground">
                    {selectedActivity?.lessonsCompleted}
                  </p>
                  <p className="text-xs text-muted-foreground font-caption">Lições</p>
                </div>
                
                <div className="text-center p-3 bg-background rounded-lg">
                  <Icon name="CheckSquare" size={20} className="text-accent mx-auto mb-1" />
                  <p className="text-lg font-heading font-semibold text-foreground">
                    {selectedActivity?.activitiesCompleted}
                  </p>
                  <p className="text-xs text-muted-foreground font-caption">Atividades</p>
                </div>
                
                <div className="text-center p-3 bg-background rounded-lg">
                  <Icon name="Target" size={20} className="text-success mx-auto mb-1" />
                  <p className="text-lg font-heading font-semibold text-foreground">
                    {selectedActivity?.streakDay ? 'Sim' : 'Não'}
                  </p>
                  <p className="text-xs text-muted-foreground font-caption">Sequência</p>
                </div>
              </div>
              
              {selectedActivity?.coursesStudied && selectedActivity?.coursesStudied?.length > 0 && (
                <div>
                  <p className="text-sm font-medium text-foreground mb-2">Cursos Estudados:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedActivity?.coursesStudied?.map((course, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-full font-caption"
                      >
                        {course}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground font-caption">
                Nenhuma atividade registrada neste dia
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LearningCalendar;